import type React from 'react';
import {useState} from 'react';
import {useFormik} from 'formik';
import {useSearchParams} from 'next/navigation';
import {useLocale, useTranslations} from 'next-intl';
import getErrorsFromResponse from '../../services/getErrorsFromResponse';
import BigCheckButton from 'src/components/onboarding/BigCheckButton';
import FormTextInput from 'src/components/FormTextInput';
import register from 'src/client/methods/register';
import BaseButton from 'src/components/BaseButton';
import {useLogin} from 'src/utils/auth/authUtils';

type Props = {
  onSignInSuccess?: (user: User) => void;
  brandStyle: BrandStyle;
  showApproveTermsStep?: boolean;
};

const SignUpForm = ({onSignInSuccess, brandStyle, showApproveTermsStep}: Props) => {
  const t = useTranslations();
  const locale = useLocale();
  const searchParams = useSearchParams();

  const emailQuery = searchParams.get('email');

  const [isLoading, setLoading] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(!showApproveTermsStep);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const {onLogin} = useLogin();

  const initialValues = {
    email: emailQuery || '',
    password: '',
    firstName: '',
    lastName: ''
  };

  const onSubmit = (values: any) => {
    if (isLoading || !termsAgreed) return;

    setLoading(true);

    return register({...values, language: locale})
      .then(({user, token}) => {
        onLogin(token, user);

        if (user && onSignInSuccess) {
          setTimeout(() => onSignInSuccess(user));
        }
      })
      .catch(async (response) => {
        setLoading(false);
        const errors = await getErrorsFromResponse(response);

        setErrorMessages(errors);
      });
  };

  const formik = useFormik({initialValues, onSubmit, enableReinitialize: true});

  return (
    <div className="big-card w-full p-5">
      <form onSubmit={formik.handleSubmit}>
        <FormTextInput id="email" brandStyle={brandStyle} label={t('common.email')} type="email" formikProps={formik} />

        <FormTextInput
          id="password"
          brandStyle={brandStyle}
          label={t('common.password')}
          type="password"
          formikProps={formik}
        />

        <FormTextInput id="firstName" brandStyle={brandStyle} label={t('common.first name')} formikProps={formik} />

        <FormTextInput id="lastName" brandStyle={brandStyle} label={t('common.last name')} formikProps={formik} />

        {showApproveTermsStep ? (
          <BigCheckButton
            className="bg-[--lighter-background] mb-5"
            value={termsAgreed}
            setValue={setTermsAgreed}
            brandStyle={brandStyle}
          >
            <span>
              {t.rich('auth.i agree with panelista terms', {
                privacy: (value) => (
                  <a href="/legal/privacy-policy" target="_blank" tabIndex={-1} rel="noreferrer">
                    {value}
                  </a>
                ),
                terms: (value) => (
                  <a href="/legal/terms-of-service" target="_blank" tabIndex={-1} rel="noreferrer">
                    {value}
                  </a>
                )
              })}
            </span>
          </BigCheckButton>
        ) : null}

        <BaseButton
          disabled={!termsAgreed}
          size="large"
          isLoading={isLoading}
          type="submit"
          variant="primary"
          className="w-full"
          style={{backgroundColor: brandStyle.primaryColor}}
          label={t('auth.sign up')}
          onClick={formik.submitForm}
        />
      </form>

      {errorMessages.map((errorMessage, i) => (
        <p className="w-full text-sm mt-3 text-[--error-color]" key={i}>
          {errorMessage}
        </p>
      ))}
    </div>
  );
};

export default SignUpForm;
